"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/Dialog";
import { Button } from "./ui/Button";
import { Input } from "./ui/Input";
import { Label } from "./ui/Label";
import { Combobox } from "@headlessui/react";
import {
  Plus,
  Save,
  Loader2,
  Check,
  ChevronDown,
  AlertCircle,
  Briefcase,
  FileText,
  Folder,
} from "lucide-react";

// Always coerce to string so .trim() never throws
const safe = (v) => (typeof v === "string" ? v : "");

export default function CreateEditModal({
  open,
  onClose,
  onSave,
  mode = "create",
  data = {},
  categories = [],
  existingCodes = new Set(),
}) {
  // Destructure primitives to avoid deep object deps in useEffect
  const dataCode        = safe(data?.code);
  const dataDescription = safe(data?.description);
  const dataCategory    = safe(data?.category);

  const [form, setForm]           = useState({ code: "", description: "", category: "" });
  const [errors, setErrors]       = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState("");
  const codeRef = useRef();

  // Reset form when modal opens or when data changes
  useEffect(() => {
    if (!open) return;

    if (mode === "edit") {
      setForm({
        code:        dataCode,
        description: dataDescription,
        category:    dataCategory,
      });
    } else {
      setForm({ code: "", description: "", category: "" });
    }
    setCategoryQuery("");
    setErrors({});
    setSubmitted(false);
    setTimeout(() => codeRef.current?.focus(), 0);
  }, [open, mode, dataCode, dataDescription, dataCategory]);

  // Inline validation
  const validate = () => {
    const e = {};
    const c = safe(form.code).trim();
    if (!c) {
      e.code = "Code is required";
    } else if (
      (mode === "create" && existingCodes.has(c)) ||
      (mode === "edit" && c !== dataCode && existingCodes.has(c))
    ) {
      e.code = "That code already exists";
    }
    if (!safe(form.description).trim()) {
      e.description = "Description is required";
    }
    if (!safe(form.category).trim()) {
      e.category = "Category is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Field updater
  const change = (field, val) => {
    setForm((f) => ({ ...f, [field]: safe(val) }));
    if (errors[field]) {
      setErrors((e) => {
        const copy = { ...e };
        delete copy[field];
        return copy;
      });
    }
  };

  // Submit handler
  const submit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  // Category filtering logic
  const searchTerm = safe(categoryQuery || form.category).trim().toLowerCase();
  const filteredCategories = categories
    .filter(Boolean)
    .filter((c) => c.toLowerCase().includes(searchTerm));
  const exactCategoryMatch =
    safe(form.category).trim().length > 0 &&
    filteredCategories.some(
      (c) => c.toLowerCase() === safe(form.category).trim().toLowerCase()
    );

  const hasErrors =
    submitting ||
    Object.keys(errors).length > 0 ||
    !safe(form.code) ||
    !safe(form.description) ||
    !safe(form.category);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 text-white border border-gray-700 sm:max-w-md w-full rounded-lg shadow-xl">
        <form onSubmit={submit} onKeyDown={(e) => e.key === "Escape" && onClose()}>
          <DialogHeader className="border-b border-gray-800 pb-4 mb-8">
            <DialogTitle className="text-xl font-semibold flex items-center">
              {mode === "create" ? (
                <>
                  <div className="bg-blue-600/30 p-2 rounded-lg mr-3 shadow-inner shadow-blue-500/20">
                    <Plus className="h-5 w-5 text-blue-400" />
                  </div>
                  Create Cost Code
                </>
              ) : (
                <>
                  <div className="bg-blue-600/30 p-2 rounded-lg mr-3 shadow-inner shadow-blue-500/20">
                    <Save className="h-5 w-5 text-blue-400" />
                  </div>
                  Edit Cost Code
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {submitted && Object.values(errors).length > 0 && (
            <div className="mb-8 p-4 bg-red-900/30 border border-red-800 rounded-lg text-sm animate-fadeIn">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 mr-2 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-300">
                    Please fix the following errors:
                  </p>
                  <ul className="list-disc ml-5 mt-2 space-y-1 text-red-200">
                    {Object.values(errors).map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-8">
            {/* Code */}
            <div className="space-y-3">
              <Label htmlFor="code" className="text-sm font-medium text-gray-300 flex items-center">
                <div className="bg-gray-800/80 p-1.5 rounded-md mr-2.5 shadow-sm">
                  <Briefcase className="h-4 w-4 text-blue-400" />
                </div>
                Code <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="relative group">
                <Input
                  id="code"
                  name="code"
                  ref={codeRef}
                  value={form.code}
                  onChange={(e) => change("code", e.target.value)}
                  className={`bg-gray-800/80 border ${
                    errors.code
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-700 focus:ring-blue-500 group-hover:border-gray-600"
                  } text-white w-full rounded-md focus:ring-2 transition-all py-2.5 px-3`}
                  disabled={submitting}
                  aria-invalid={!!errors.code}
                  placeholder="Enter unique code identifier"
                />
                {errors.code && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              {errors.code && (
                <p className="text-xs text-red-500 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" /> {errors.code}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-3">
              <Label
                htmlFor="description"
                className="text-sm font-medium text-gray-300 flex items-center"
              >
                <div className="bg-gray-800/80 p-1.5 rounded-md mr-2.5 shadow-sm">
                  <FileText className="h-4 w-4 text-blue-400" />
                </div>
                Description <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="relative group">
                <textarea
                  id="description"
                  name="description"
                  value={form.description}
                  onChange={(e) => change("description", e.target.value)}
                  className={`bg-gray-800/80 border ${
                    errors.description
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-700 focus:ring-blue-500 group-hover:border-gray-600"
                  } text-white w-full rounded-md focus:ring-2 transition-all py-2.5 px-3 min-h-[80px] resize-none`}
                  disabled={submitting}
                  aria-invalid={!!errors.description}
                  placeholder="Enter detailed description"
                />
                {errors.description && (
                  <div className="absolute top-3 right-3 pointer-events-none">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  </div>
                )}
              </div>
              {errors.description && (
                <p className="text-xs text-red-500 mt-1 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" /> {errors.description}
                </p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-3">
              <Label htmlFor="category" className="text-sm font-medium text-gray-300 flex items-center">
                <div className="bg-gray-800/80 p-1.5 rounded-md mr-2.5 shadow-sm">
                  <Folder className="h-4 w-4 text-blue-400" />
                </div>
                Category <span className="text-red-500 ml-1">*</span>
              </Label>
              <div className="group">
                <Combobox
                  value={form.category}
                  onChange={(v) => change("category", v)}
                  disabled={submitting}
                >
                  <div className="relative">
                    <div
                      className={`relative w-full cursor-default overflow-hidden rounded-md bg-gray-800/80 border ${
                        errors.category
                          ? "border-red-500"
                          : "border-gray-700 group-hover:border-gray-600"
                      } text-left focus-within:ring-2 focus-within:ring-blue-500 transition-all`}
                    >
                      <Combobox.Input
                        className="w-full border-none bg-transparent py-2.5 pl-3 pr-10 text-white focus:ring-0"
                        onChange={(e) => {
                          change("category", e.target.value);
                          setCategoryQuery(e.target.value);
                        }}
                        placeholder="Select or create a category..."
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>
                    </div>

                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-800 border border-gray-700 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {filteredCategories.length === 0 && safe(form.category).trim() !== "" ? (
                        <div className="relative cursor-pointer select-none py-3 px-4 text-blue-400 hover:bg-gray-700">
                          <Plus className="h-4 w-4 mr-2" />
                          Create "{safe(form.category).trim()}"
                        </div>
                      ) : (
                        filteredCategories.map((category) => (
                          <Combobox.Option
                            key={category}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-3 pl-10 pr-4 ${
                                active ? "bg-blue-600 text-white" : "text-gray-200"
                              }`
                            }
                            value={category}
                          >
                            {({ selected, active }) => (
                              <>
                                <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                  {category}
                                </span>
                                {selected && (
                                  <span
                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                      active ? "text-white" : "text-blue-400"
                                    }`}
                                  >
                                    <Check className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                )}
                              </>
                            )}
                          </Combobox.Option>
                        ))
                      )}

                      {!exactCategoryMatch && safe(form.category).trim().length > 0 && filteredCategories.length > 0 && (
                        <Combobox.Option
                          className={() =>
                            "relative cursor-pointer select-none py-3 pl-10 pr-4 bg-blue-600/20 hover:bg-blue-600 hover:text-white text-blue-400"
                          }
                          value={safe(form.category).trim()}
                        >
                          <span className="block truncate font-medium flex items-center">
                            <Plus className="h-4 w-4 mr-2" />
                            Create "{safe(form.category).trim()}"
                          </span>
                        </Combobox.Option>
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>

                {errors.category && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" /> {errors.category}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-10 pt-5 border-t border-gray-800 flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
              className="bg-gray-800/80 border-gray-700 hover:bg-gray-700 text-white transition-colors px-5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={hasErrors}
              className={`flex items-center transition-colors px-5 ${
                hasErrors
                  ? "bg-blue-900/50 cursor-not-allowed text-gray-300"
                  : "bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-900/30"
              }`}
            >
              {submitting ? (
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
              ) : mode === "create" ? (
                <Plus className="h-5 w-5 mr-2" />
              ) : (
                <Save className="h-5 w-5 mr-2" />
              )}
              {mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
